import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { documentsService } from '../services/documents.service';
import {
  Document,
  DocumentsQueryParams,
  UploadedDocument,
  TextDocumentCreate,
  CredentialDocumentCreate,
} from '../types/documents.types';
import { toast } from 'sonner';
import { DocumentCredentialsResponse } from '../types';
import { useOrganisationsStore } from '@/store/organisations.store';

// Query Keys
export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (params: DocumentsQueryParams) =>
    [...documentKeys.lists(), params] as const,
  detail: (uuid: string) => [...documentKeys.all, 'detail', uuid] as const,
};

// Hook pour la liste des documents
export const useDocuments = (params: DocumentsQueryParams) => {
  const orgUuid = useOrganisationsStore(s => s.currentOrganisation?.uuid);
  return useQuery({
    queryKey: documentKeys.list({ ...params, organisationUuid: orgUuid }),
    queryFn: () => documentsService.getDocuments(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook pour un document spécifique
export const useDocument = (uuid: string | undefined) => {
  return useQuery({
    queryKey: documentKeys.detail(uuid!),
    queryFn: () => documentsService.getDocument(uuid!),
    enabled: !!uuid,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook pour l'upload de documents
export const useDocumentUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documents: UploadedDocument[]) =>
      documentsService.uploadDocuments(documents),
    onSuccess: (response, documents) => {
      if (response.success) {
        toast.success(
          `${documents.length} document(s) téléversé(s) avec succès`
        );

        // Invalidate queries pour actualiser les listes
        const firstDoc = documents[0];
        queryClient.invalidateQueries({
          queryKey: documentKeys.list({
            context: firstDoc.context,
            contextUuid: firstDoc.contextUuid,
          }),
        });
      } else {
        toast.error(response.message || 'Erreur lors du téléversement');
      }
    },
    onError: cause => {
      const error = cause as HttpApiError;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors du téléversement des documents';
      toast.error(message);
    },
  });
};

// Hook pour la suppression
export const useDocumentDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid: string) => {
      console.log('useDocumentDelete mutationFn called with uuid:', uuid);
      return documentsService.deleteDocument(uuid);
    },
    onSuccess: () => {
      console.log('useDocumentDelete onSuccess');
      toast.success('Document supprimé avec succès');
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
    onError: (error: HttpApiError) => {
      console.error('useDocumentDelete onError:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la suppression';
      toast.error(message);
    },
  });
};

// Hook pour le téléchargement
export const useDocumentDownload = () => {
  return useMutation({
    mutationFn: async (document: Document) => {
      console.log(
        'useDocumentDownload mutationFn called with document:',
        document
      );
      const blob = await documentsService.downloadDocument(document.uuid);
      console.log('Blob received:', blob);

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.name;
      window.document.body.appendChild(link);
      console.log('Triggering download for:', document.name);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      console.log('Download triggered successfully');
    },
    onError: (error: HttpApiError) => {
      console.error('useDocumentDownload onError:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors du téléchargement';
      toast.error(message);
    },
  });
};

// Hook pour la mise à jour des métadonnées
export const useDocumentUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      updates,
    }: {
      uuid: string;
      updates: Partial<Document>;
    }) => documentsService.updateDocument(uuid, updates),
    onSuccess: (_response, variables) => {
      toast.success('Document mis à jour avec succès');
      queryClient.invalidateQueries({
        queryKey: documentKeys.detail(variables.uuid),
      });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la mise à jour';
      toast.error(message);
    },
  });
};

// Hook pour la création de documents texte
export const useDocumentCreateText = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TextDocumentCreate) =>
      documentsService.createTextDocument(data),
    onSuccess: (_response, variables) => {
      toast.success('Document texte créé avec succès');
      queryClient.invalidateQueries({
        queryKey: documentKeys.list({
          context: variables.context,
          contextUuid: variables.contextUuid,
        }),
      });
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la création du document texte';
      toast.error(message);
    },
  });
};

// Hook pour la création d'identifiants
export const useDocumentCreateCredential = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CredentialDocumentCreate) =>
      documentsService.createCredentialDocument(data),
    onSuccess: (_response, variables) => {
      toast.success('Identifiants créés avec succès');
      queryClient.invalidateQueries({
        queryKey: documentKeys.list({
          context: variables.context,
          contextUuid: variables.contextUuid,
        }),
      });
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la création des identifiants';
      toast.error(message);
    },
  });
};

interface DecryptCredentialsPayload {
  uuid: string;
  password: string;
}

export const useDocumentCredentialsDecrypt = () => {
  return useMutation<
    DocumentCredentialsResponse,
    unknown,
    DecryptCredentialsPayload
  >({
    mutationFn: ({ uuid, password }) =>
      documentsService.decryptDocumentCredentials(uuid, password),
    onError: cause => {
      const error = cause as HttpApiError;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to decrypt credentials';
      toast.error(message);
    },
  });
};

// Hook pour l'export ZIP des documents
export const useDocumentExportZip = () => {
  return useMutation({
    mutationFn: async (params: DocumentsQueryParams) => {
      const blob = await documentsService.exportDocumentsZip(params);

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.download = `export-documents-${date}.zip`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast.success('Documents exportés avec succès');
    },
    onError: async (error: HttpApiError) => {
      let message = "Erreur lors de l'export des documents";
      // Quand responseType est 'blob', le corps d'erreur est un Blob qu'il faut parser
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json.message || message;
        } catch {
          // ignore parse error
        }
      } else {
        message = error?.response?.data?.message || error?.message || message;
      }
      toast.error(message);
    },
  });
};
